import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { API_BASE } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const body = response.data;
      // Backend returns { success, message, data: { token, user } }
      const data = body?.data ?? body;
      const token = data?.token;
      const user = data?.user;
      if (!token || !user) {
        return { success: false, message: body?.message || 'Invalid response from server' };
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      return { success: true };
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || !error.response) {
        const isProduction = /^https:\/\//.test(API_BASE || '');
        const message = isProduction
          ? `Cannot reach API at ${API_BASE}. Check that the API is deployed and the domain document root is set to backend/public.`
          : 'Cannot reach backend. Start it with: cd backend && php spark serve --port=8080';
        return { success: false, message };
      }
      const msg = error.response?.data?.message || error.response?.data?.error || 'Login failed';
      return { success: false, message: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  /**
   * After academy CMS SSO: stores JWT from smdeducationacademy API and a minimal user for this SPA.
   * School ERP API calls still use REACT_APP_BACKEND_URL; academy token is for shared CMS admin identity.
   */
  const applyAcademySsoSession = useCallback(({ token, username, adminId }) => {
    if (!token || !username) return;
    const id = adminId != null && String(adminId) !== '' ? String(adminId) : 'academy-admin';
    const sessionUser = {
      id,
      username,
      email: `${username}@academy-sso.local`,
      role_slug: 'super_admin',
    };
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(sessionUser));
    setUser(sessionUser);
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.role_slug === 'super_admin' || user?.role_slug === 'school_admin';

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, isAuthenticated, isAdmin, applyAcademySsoSession }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
